import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Grid2 as Grid,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SummaryContent } from '../components/bookings/BookingFormSummary';
import PageTitle from '../components/coreLayout/PageTitle';
import Seo from '../components/coreLayout/Seo';
import { BookingLookupStatus, fetchLiveBooking } from '../utils/functions/bookingLookup';
import { amountUiStr } from '../utils/functions/common';
import IsMountedWrapper from '../utils/functions/isMountedWrapper';
import { findProductForBooking, isPackageProduct } from '../utils/functions/productLookup';
import { Tour } from '../utils/models/bookings';
import { BookingStore } from '../utils/stores/BookingStore';
import { ProductsStore } from '../utils/stores/ProductsStore';
import { baseProducts } from '../utils/stores/productCodes';

const sx = {
    container: {
        maxWidth: { sm: 700, md: 940 },
        px: { xs: 1.5, sm: 3 },
        pt: { xs: 2, md: 3 },
        pb: { xs: 5, md: 7 },
    },
    // Success hero ---------------------------------------------------------
    hero: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: '18px', sm: '22px' },
        p: { xs: 3, sm: 4 },
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(255,233,201,0.7) 0%, rgba(255,255,255,1) 72%)',
        border: '1px solid rgba(247, 97, 0, 0.16)',
        mb: { xs: 2.5, md: 3 },
    },
    heroIcon: {
        fontSize: { xs: 52, sm: 60 },
        color: 'primary.main',
        mb: 1,
    },
    heroTitle: {
        fontWeight: 600,
        fontSize: { xs: 26, sm: 32 },
        color: 'primary.dark',
        lineHeight: 1.1,
    },
    heroSubtitle: {
        fontSize: { xs: 15, sm: 16 },
        fontWeight: 300,
        opacity: 0.85,
        mt: 1,
        maxWidth: 460,
        mx: 'auto',
        lineHeight: 1.5,
    },
    refPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        mt: 2.5,
        pl: 2,
        pr: 0.5,
        py: 0.5,
        borderRadius: '999px',
        bgcolor: 'common.white',
        border: '1px solid rgba(247, 97, 0, 0.22)',
        boxShadow: '0 6px 18px rgba(247, 97, 0, 0.10)',
    },
    refPillLabel: {
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: 'text.soft',
        opacity: 0.75,
    },
    refPillValue: {
        fontSize: { xs: 17, sm: 19 },
        fontWeight: 600,
        letterSpacing: '0.05em',
        color: 'primary.dark',
        lineHeight: 1,
    },
    // Cards ----------------------------------------------------------------
    grid: {
        rowGap: { xs: 2.5, md: 0 },
    },
    leftCol: { pr: { md: 1.5 }, display: { md: 'flex' } },
    rightCol: { pl: { md: 1.5 }, display: { md: 'flex' } },
    rightStack: {
        width: '100%',
        height: { md: '100%' },
        justifyContent: { md: 'space-between' },
    },
    card: {
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.07)',
        width: '100%',
    },
    ticketCard: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    cardPad: {
        p: { xs: 2, sm: 2.5 },
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontWeight: 600,
        fontSize: { xs: 15, sm: 16 },
        letterSpacing: '0.02em',
        color: 'text.primary',
        '& svg': { fontSize: 20, color: 'primary.main' },
    },
    heroImg: {
        width: '100%',
        height: { xs: 170, sm: 200 },
        objectFit: 'cover',
        display: 'block',
    },
    productName: {
        fontWeight: 600,
        fontSize: { xs: 20, sm: 23 },
        lineHeight: 1.18,
    },
    productTagline: {
        fontSize: { xs: 14, sm: 15 },
        fontWeight: 300,
        opacity: 0.82,
        mt: 0.5,
    },
    // Infographic trip facts ----------------------------------------------
    legLabel: {
        fontSize: { xs: 13, sm: 14 },
        fontWeight: 600,
        color: 'primary.dark',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        mt: 2,
        mb: 1,
    },
    tileRow: {
        display: 'flex',
        gap: { xs: 1, sm: 1.5 },
        flexWrap: 'wrap',
    },
    tile: {
        p: { xs: 1.5, sm: 1.75 },
        borderRadius: '12px',
        bgcolor: 'rgba(255, 183, 137, 0.12)',
        border: '1px solid rgba(247, 97, 0, 0.12)',
    },
    tileEmphasis: {
        bgcolor: 'rgba(247, 97, 0, 0.10)',
        border: '1px solid rgba(247, 97, 0, 0.30)',
        p: { xs: 1.75, sm: 2 },
    },
    tileLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: 'text.soft',
        opacity: 0.85,
        mb: 0.5,
        '& svg': { fontSize: 15, color: 'primary.main' },
    },
    tileLabelEmphasis: {
        color: 'primary.dark',
        opacity: 1,
        '& svg': { fontSize: 17, color: 'primary.main' },
    },
    tileValue: {
        fontSize: { xs: 15, sm: 16 },
        fontWeight: 500,
        color: 'text.primary',
        lineHeight: 1.25,
    },
    tileValueEmphasis: {
        fontSize: { xs: 17, sm: 19 },
        fontWeight: 600,
    },
    tileSub: {
        fontSize: 12.5,
        fontWeight: 400,
        opacity: 0.7,
        mt: 0.25,
    },
    // Payment --------------------------------------------------------------
    payRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        py: 0.5,
    },
    payLabel: {
        fontSize: { xs: 13.5, sm: 14 },
        fontWeight: 300,
        opacity: 0.7,
    },
    payValue: {
        fontSize: { xs: 14, sm: 15 },
        fontWeight: 500,
        textAlign: 'right',
        wordBreak: 'break-word',
    },
    // Package notice -------------------------------------------------------
    packageNotice: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 1.5,
        p: { xs: 2, sm: 2.5 },
        borderRadius: '14px',
        bgcolor: 'rgba(2, 136, 209, 0.07)',
        border: '1px solid rgba(2, 136, 209, 0.2)',
        mb: { xs: 2.5, md: 3 },
    },
    packageNoticeIcon: {
        color: 'info.main',
        fontSize: 22,
        flexShrink: 0,
        mt: '2px',
    },
    packageNoticeText: {
        fontSize: { xs: 14, sm: 15 },
        lineHeight: 1.55,
        opacity: 0.9,
    },
    disclaimer: {
        mt: { xs: 3, md: 4 },
        px: { xs: 0.5, sm: 1 },
        fontWeight: 300,
        fontSize: { xs: 12.5, sm: 13.5 },
        lineHeight: 1.6,
        opacity: 0.7,
        textAlign: 'center',
    },
    findPaper: {
        p: { xs: 2.5, sm: 4 },
        borderRadius: '16px',
        width: '100%',
        border: '1px solid rgba(0,0,0,0.07)',
    },
    bodyText: {
        fontSize: { xs: 15, sm: 16 },
        lineHeight: 1.5,
        fontWeight: 300,
        opacity: 0.85,
    },
} as const;

const StatTile = ({
    icon,
    label,
    value,
    sub,
    fullWidth,
    emphasis,
}: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    sub?: string;
    fullWidth?: boolean;
    emphasis?: boolean;
}) => (
    <Box
        sx={[
            sx.tile,
            fullWidth ? { flexBasis: '100%', width: '100%' } : { flex: 1, minWidth: 128 },
            emphasis && sx.tileEmphasis,
        ]}
    >
        <Box sx={[sx.tileLabel, emphasis && sx.tileLabelEmphasis]}>
            {icon}
            {label}
        </Box>
        <Typography sx={[sx.tileValue, emphasis && sx.tileValueEmphasis]}>{value}</Typography>
        {sub && <Typography sx={[sx.tileSub, emphasis && { opacity: 0.85 }]}>{sub}</Typography>}
    </Box>
);

// Trip facts as scannable tiles. Meeting Point is the #1 instruction (where +
// when to show up), so it's the emphasised hero tile; tour date/time follow.
const TourFacts = ({ tour, label }: { tour?: Tour; label?: string }) => {
    if (!tour?.startTimeLocal) {
        return null;
    }
    const start = moment(tour.startTimeLocal).utcOffset(10);
    const hasPickup =
        tour.pickUpTime && !moment(tour.pickUpTime).utcOffset(10).isSame(moment(tour.startTimeLocal).utcOffset(10));
    const meetingTime = hasPickup ? moment(tour.pickUpTime).utcOffset(10).format('h:mm a') : undefined;
    const meetingSub = [start.format('ddd, D MMM YYYY'), meetingTime].filter(Boolean).join('  ·  ');

    return (
        <Box>
            {label && <Typography sx={sx.legLabel}>{label}</Typography>}
            <Stack spacing={{ xs: 1, sm: 1.5 }}>
                {tour.pickUpLocation && (
                    <StatTile
                        emphasis
                        fullWidth
                        icon={<PlaceRoundedIcon />}
                        label="Meeting Point"
                        value={tour.pickUpLocation}
                        sub={meetingSub}
                    />
                )}
                <Box sx={sx.tileRow}>
                    <StatTile icon={<EventRoundedIcon />} label="Date" value={start.format('ddd, D MMM YYYY')} />
                    <StatTile icon={<AccessTimeRoundedIcon />} label="Tour time" value={start.format('h:mm a')} />
                </Box>
            </Stack>
        </Box>
    );
};

const PaymentCard = () => {
    const bookingController = useContext(BookingStore);
    const booking = bookingController?.completedBookings?.data?.booking;
    const payment = booking?.payment;
    if (!payment?.date && !payment?.paymentId && payment?.amount == null) {
        return null;
    }

    return (
        <Paper elevation={0} sx={sx.card}>
            <Box sx={sx.cardPad}>
                <Box sx={sx.cardHeader}>
                    <ReceiptLongRoundedIcon />
                    Payment
                </Box>
                <Box sx={{ mt: 1.5 }}>
                    {payment?.date && (
                        <Box sx={sx.payRow}>
                            <Typography sx={sx.payLabel}>Paid on</Typography>
                            <Typography sx={sx.payValue}>
                                {moment(payment.date).format('D MMM YYYY, h:mm a')}
                            </Typography>
                        </Box>
                    )}
                    {payment?.paymentId && (
                        <Box sx={sx.payRow}>
                            <Typography sx={sx.payLabel}>Merchant ref</Typography>
                            <Typography sx={sx.payValue}>{payment.paymentId}</Typography>
                        </Box>
                    )}
                    {payment?.amount != null && (
                        <Box sx={sx.payRow}>
                            <Typography sx={sx.payLabel}>Amount paid</Typography>
                            <Typography sx={[sx.payValue, { color: 'primary.dark', fontWeight: 600 }]}>
                                $ {amountUiStr(payment.amount)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default function BookingConfirmationPage() {
    const bookingController = useContext(BookingStore);
    const booking = bookingController?.completedBookings?.data.booking;
    const packageBooking = bookingController?.completedBookings?.data.package;
    const productController = useContext(ProductsStore);
    const { ref } = useParams() as any;
    const isMounted = IsMountedWrapper();
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // The cached (localStorage) booking acts as lightweight proof that this browser
    // made the booking - if it matches the ref in the URL we show it straight away.
    const hasCachedBooking = ref === booking?.bookingResponse?.refNum;

    const [surname, setSurname] = useState('');
    const [lookupStatus, setLookupStatus] = useState<BookingLookupStatus | 'idle' | 'searching'>('idle');
    // True when the displayed booking was fetched live from Rezdy (find-my-booking),
    // rather than read from the localStorage snapshot taken at booking time.
    const [isLiveBooking, setIsLiveBooking] = useState(false);
    const [copied, setCopied] = useState(false);

    // No cached copy (shared link, new device, cleared cache): look the booking up
    // live from Rezdy, gated by the surname so a bare ref can't expose someone's details.
    const handleFindBooking = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!surname.trim()) {
            return;
        }
        setLookupStatus('searching');
        const result = await fetchLiveBooking(ref, surname);
        if (result.status === BookingLookupStatus.FOUND && result.booking) {
            bookingController.completedBookings.setData({ booking: result.booking }, isMounted);
            setIsLiveBooking(true);
            setLookupStatus('idle');
        } else {
            setLookupStatus(result.status);
        }
    };

    const copyRef = async () => {
        try {
            await navigator.clipboard.writeText(booking?.bookingResponse?.refNum || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* clipboard unavailable - ignore */
        }
    };

    const isSearching = lookupStatus === 'searching';

    // A package is stored as two separate Rezdy bookings, so a live lookup can only
    // rebuild the first leg. Resolve the booked product from the loaded catalogue
    // (by product code or name) to detect packages and name each leg for the notice.
    const bookedProduct =
        findProductForBooking(productController.products, booking?.tour) ||
        findProductForBooking(baseProducts, booking?.tour);
    const isPackageLiveBooking = isLiveBooking && isPackageProduct(bookedProduct);

    const storeProduct = productController?.products.find((prod) => prod.productCode === booking?.tour?.productCode);
    const heroImage =
        storeProduct?.photos?.[0]?.mediumURL ||
        storeProduct?.photos?.[0]?.url ||
        storeProduct?.photos?.[0]?.thumbnailURL;
    const productTagline =
        baseProducts.find((prod) => prod.productCode === booking?.tour?.productCode)?.tagline || storeProduct?.tagline;
    const firstName = booking?.customer?.firstName?.trim();

    return (
        <>
            <Seo
                title="Booking Confirmation | GSL Aviation"
                description="View your GSL Aviation tour booking confirmation and payment details."
                noindex
            />
            {hasCachedBooking ? (
                <Container maxWidth="md" sx={sx.container}>
                    {isPackageLiveBooking && (
                        <Box sx={sx.packageNotice} role="note">
                            <InfoOutlinedIcon sx={sx.packageNoticeIcon} />
                            <Typography sx={sx.packageNoticeText}>
                                This is a package booking, and the details below will show the scenic flight departure
                                only. Please check your email confirmation or{' '}
                                <Link to="/contact" style={{ color: 'inherit', fontWeight: 600 }}>
                                    contact our team
                                </Link>{' '}
                                to check the package tour departure.
                            </Typography>
                        </Box>
                    )}

                    {/* 1. Success hero - immediate reassurance + reference */}
                    <Box sx={sx.hero}>
                        <CheckCircleRoundedIcon sx={sx.heroIcon} />
                        <Typography component="h1" sx={sx.heroTitle}>
                            Booking confirmed
                        </Typography>
                        <Typography sx={sx.heroSubtitle}>
                            You&apos;re all set{firstName ? `, ${firstName}` : ''}! Get ready to travel with GSL
                            Aviation.
                        </Typography>
                        <Box sx={sx.refPill}>
                            <ConfirmationNumberRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography sx={sx.refPillLabel}>Booking reference</Typography>
                                <Typography sx={sx.refPillValue}>{booking.bookingResponse.refNum}</Typography>
                            </Box>
                            <Tooltip title={copied ? 'Copied!' : 'Copy reference'} placement="top" arrow>
                                <IconButton onClick={copyRef} size="small" aria-label="Copy booking reference">
                                    <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Grid container justifyContent="center" sx={sx.grid}>
                        {/* 2. The "ticket" - what you booked + when & where (infographic) */}
                        <Grid size={{ xs: 12, md: 7 }} sx={sx.leftCol}>
                            <Paper elevation={0} sx={[sx.card, sx.ticketCard]}>
                                {productController.isLoading ? (
                                    <Skeleton variant="rectangular" sx={sx.heroImg} />
                                ) : (
                                    heroImage && (
                                        <Box
                                            component="img"
                                            src={heroImage}
                                            alt={booking.tour.productName || 'Your tour'}
                                            sx={sx.heroImg}
                                        />
                                    )
                                )}
                                <Box sx={sx.cardPad}>
                                    <Typography sx={sx.productName}>{booking.tour.productName}</Typography>
                                    {productTagline && <Typography sx={sx.productTagline}>{productTagline}</Typography>}

                                    {booking.isPackage ? (
                                        <>
                                            <TourFacts tour={booking.tour} label={booking.packageProductNames?.[0]} />
                                            {packageBooking?.tour && (
                                                <TourFacts
                                                    tour={packageBooking.tour}
                                                    label={booking.packageProductNames?.[1]}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <Box sx={{ mt: 2 }}>
                                            <TourFacts tour={booking.tour} />
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* 3. Order summary + 4. payment (de-emphasized, last) */}
                        <Grid size={{ xs: 12, md: 5 }} sx={sx.rightCol}>
                            <Stack spacing={2.5} sx={sx.rightStack}>
                                <Paper elevation={0} sx={sx.card}>
                                    <Box sx={sx.cardPad}>
                                        <Box sx={sx.cardHeader}>
                                            <ReceiptLongRoundedIcon />
                                            Order summary
                                        </Box>
                                        <Box sx={{ mt: 0.5 }}>
                                            <SummaryContent isConfirmation pricingOnly />
                                        </Box>
                                    </Box>
                                </Paper>
                                <PaymentCard />
                            </Stack>
                        </Grid>
                    </Grid>

                    <Typography component="div" sx={sx.disclaimer}>
                        {isLiveBooking ? (
                            <>
                                These are your current booking details, retrieved live from our reservations system.
                                Whenever there&apos;s changes to your booking, our team will also send a new
                                confirmation via email. Your ticket is subject to our{' '}
                                <Link to="/termsandconditions">terms &amp; conditions.</Link>
                            </>
                        ) : (
                            <>
                                Please note that if your booking details have changed at any time since the booking was
                                made, the details shown here may not reflect the latest version. Whenever there&apos;s
                                changes to your booking, our team will send a new confirmation via email. Your ticket is
                                subject to our <Link to="/termsandconditions">terms &amp; conditions.</Link>
                            </>
                        )}
                    </Typography>
                </Container>
            ) : (
                <>
                    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 3 } }}>
                        <PageTitle title="Find your booking" tagline="Look up your GSL Aviation tour" compact />
                    </Container>
                    <Container maxWidth="sm" sx={{ minHeight: '55vh', px: { xs: 1.5, sm: 3 } }}>
                        <Grid container justifyContent="center" sx={{ pt: { xs: 3, sm: 4 }, pb: { xs: 5, sm: 7 } }}>
                            <Paper elevation={0} sx={sx.findPaper}>
                                <Typography sx={{ ...sx.bodyText, pb: 2.5 }}>
                                    To view the details for booking <strong>{ref}</strong>, please confirm the last name
                                    on the booking.
                                </Typography>
                                <Box component="form" onSubmit={handleFindBooking}>
                                    <TextField
                                        fullWidth
                                        label="Last name"
                                        value={surname}
                                        onChange={(e) => {
                                            setSurname(e.target.value);
                                            if (lookupStatus !== 'idle' && lookupStatus !== 'searching') {
                                                setLookupStatus('idle');
                                            }
                                        }}
                                        disabled={isSearching}
                                        autoFocus
                                    />
                                    {lookupStatus === BookingLookupStatus.NOT_FOUND && (
                                        <Typography color="error" sx={{ mt: 1.5, fontSize: 14, lineHeight: 1.45 }}>
                                            We couldn&apos;t find a booking matching that reference and last name.
                                            Please double-check and try again.
                                        </Typography>
                                    )}
                                    {lookupStatus === BookingLookupStatus.ERROR && (
                                        <Typography color="error" sx={{ mt: 1.5, fontSize: 14, lineHeight: 1.45 }}>
                                            Something went wrong looking up your booking. Please try again in a moment.
                                        </Typography>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disableElevation
                                        disabled={isSearching || !surname.trim()}
                                        sx={{ mt: 3, py: 1.25 }}
                                    >
                                        {isSearching ? (
                                            <CircularProgress size={24} sx={{ color: '#fff' }} />
                                        ) : (
                                            'Find my booking'
                                        )}
                                    </Button>
                                </Box>
                                <Typography sx={{ mt: 3, fontSize: 13, opacity: 0.7, lineHeight: 1.5 }} component="div">
                                    Having trouble? Try the <Link to="/contact">contact page</Link> and our team can
                                    help.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Container>
                </>
            )}
        </>
    );
}
